import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from './ConfirmationModal';

interface PreviewSMSPageProps {
  message: string;
  recipients: string[];
  onEdit: () => void;
  onSend: () => void;
}

const PreviewSMSPage: React.FC<PreviewSMSPageProps> = ({
  message,
  recipients,
  onEdit,
  onSend,
}) => {
  const navigate = useNavigate();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  // Calculate message stats
  const totalMessages = Math.ceil(message.length / 160);
  const totalCredits = recipients.length * totalMessages;

  // Get current date and time in Zimbabwe timezone
  const now = new Date();
  const deliveryDate = now.toLocaleDateString('en-US', { 
    timeZone: 'Africa/Harare',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const deliveryTime = now.toLocaleTimeString('en-US', {
    timeZone: 'Africa/Harare',
    hour: '2-digit',
    minute: '2-digit'
  });

  const handleLaunch = () => {
    onSend();
    setIsConfirmationOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-['Helvetica_Neue']">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-800"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-xl font-bold">Broadcast / Preview</h1>
            </div>
            <div className="flex items-center space-x-4">
              <a href="#pricing" className="text-[#FF5722] hover:text-[#F4511E]">PRICING</a>
              <button className="bg-[#FF5722] text-white px-4 py-2 rounded hover:bg-[#F4511E] transition-colors">
                ADD FUNDS
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-3 gap-8">
          {/* SMS Preview Section */}
          <div className="col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium">SMS</h2>
                <button 
                  onClick={onEdit}
                  className="text-[#FF5722] hover:text-[#F4511E]"
                >
                  EDIT CONTENT
                </button>
              </div>

              {/* Phone Preview */}
              <div className="max-w-xs mx-auto border-8 border-gray-800 rounded-3xl p-4 bg-gray-100">
                <div className="bg-white rounded-xl p-4 shadow-inner min-h-[200px]">
                  <div className="flex items-center mb-4">
                    <span className="text-sm font-medium">ServiceSMS</span>
                    <svg className="w-4 h-4 ml-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-3 mb-2 inline-block max-w-[90%]">
                    <p className="text-sm">{message}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="space-y-6">
            {/* Ready to Send Status */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center space-x-2 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium">Ready to be sent</span>
              </div>
            </div>

            {/* Recipients Info */}
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <div className="flex flex-col space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Recipients</span>
                </div>
                <div className="flex flex-col space-y-1">
                  {recipients.slice(0, 5).map((r, index) => (
                    <span key={index} className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {r.split('@')[0]}
                    </span>
                  ))}
                  {recipients.length > 5 && (
                    <span className="text-xs text-gray-500 italic">
                      ... and {recipients.length - 5} more recipient(s)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total recipients</span>
                <span className="font-medium">{recipients.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total messages</span>
                <div className="flex items-center space-x-1">
                  <span className="font-medium">{totalMessages}</span>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Credits</span>
                <div className="flex items-center space-x-1">
                  <span className="font-medium">{totalCredits.toLocaleString()}</span>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Delivery time window</span>
                <span className="text-sm">Everyday 00 - 24</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Time zone</span>
                <span className="text-sm">Zimbabwe / Harare (CAT)</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={onEdit}
                className="flex-1 bg-gray-100 text-gray-600 px-4 py-2 rounded hover:bg-gray-200 transition-colors"
              >
                BACK TO SETTINGS
              </button>
              <button
                onClick={onSend}
                className="flex-1 bg-[#FF5722] text-white px-4 py-2 rounded hover:bg-[#F4511E] transition-colors"
              >
                SEND TEST
              </button>
            </div>
            <button
              onClick={handleLaunch}
              className="w-full bg-[#FF5722] text-white px-4 py-2 rounded hover:bg-[#F4511E] transition-colors"
            >
              LAUNCH
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        totalRecipients={recipients.length}
        totalMessages={totalMessages}
        totalCredits={totalCredits}
        deliveryDate={deliveryDate}
        deliveryTime={deliveryTime}
      />
    </div>
  );
};

export default PreviewSMSPage; 